import { TAG_VALUE_MAX } from '@headline/schemas'
import type { NerEntity, Tag } from '@headline/schemas'
import { createFileRoute, Link } from '@tanstack/react-router'
import { ArrowLeft, Plus, Tag as TagIcon } from 'lucide-react'
import { useState } from 'react'
import { TagChipView } from '@/components/topics/chips'
import { DeleteTagDialog } from '@/components/topics/delete-tag-dialog'
import { EditTagDialog } from '@/components/topics/edit-tag-dialog'
import { ENTITY_META, NER_ENTITIES } from '@/components/topics/ner'
import {
  EntityFilter,
  RESOURCE_SEARCH_THRESHOLD,
  ResourceSearchInput,
} from '@/components/topics/resource-controls'
import { PageHeader } from '@/components/PageHeader'
import { Button, buttonVariants } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { tagsQuery } from '@/lib/queries'
import { useResourceLibrary } from '@/lib/use-resource-library'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/app/accounts/$accountId_/topics/$subaccountId/resources')({
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData(tagsQuery(params.subaccountId)),
  component: SharedResources,
})

function SharedResources() {
  const { accountId, subaccountId } = Route.useParams()
  const { tags, createTag, updateTag, deleteTag } = useResourceLibrary(subaccountId)
  const [tagQuery, setTagQuery] = useState('')
  const [tagFilter, setTagFilter] = useState<NerEntity | 'all'>('all')
  const [pendingTag, setPendingTag] = useState<Tag | null>(null)
  const [editingTag, setEditingTag] = useState<Tag | null>(null)

  const tq = tagQuery.trim().toLowerCase()
  const filteredTags = tags.filter(
    (tag) =>
      (tagFilter === 'all' || tag.entity === tagFilter) &&
      (!tq ||
        tag.value.toLowerCase().includes(tq) ||
        ENTITY_META[tag.entity].label.toLowerCase().includes(tq)),
  )
  const distinctEntities = NER_ENTITIES.filter((option) =>
    tags.some((tag) => tag.entity === option),
  )
  const showTagControls = tags.length > RESOURCE_SEARCH_THRESHOLD

  return (
    <div className="mx-auto flex w-full max-w-3xl animate-fade-in flex-col gap-6">
      <PageHeader
        title="Shared resources"
        description="Tags live at the client level — define them once, reuse them across every topic."
        className="mb-0"
        actions={
          <Link
            to="/app/accounts/$accountId/topics/$subaccountId"
            params={{ accountId, subaccountId }}
            className={cn(buttonVariants({ variant: 'outline' }))}
          >
            <ArrowLeft className="size-4" /> Topics
          </Link>
        }
      />

      <Card>
        <CardContent className="flex flex-col gap-3 p-5">
          <div className="flex items-center gap-1.5 text-sm font-medium">
            <TagIcon className="size-4 text-muted-foreground" /> Tags
            <span className="rounded-full bg-muted px-1.5 text-xs text-muted-foreground tabular-nums">
              {tags.length}
            </span>
          </div>

          <AddTagRow createTag={createTag} />

          {showTagControls ? (
            <div className="flex flex-col gap-2">
              <ResourceSearchInput
                value={tagQuery}
                onChange={setTagQuery}
                placeholder="Search tags…"
              />
              {distinctEntities.length >= 2 ? (
                <EntityFilter
                  entities={distinctEntities}
                  active={tagFilter}
                  onChange={setTagFilter}
                />
              ) : null}
            </div>
          ) : null}

          {tags.length === 0 ? (
            <p className="text-sm text-muted-foreground">No tags yet.</p>
          ) : filteredTags.length === 0 ? (
            <p className="text-sm text-muted-foreground">No tags match your search.</p>
          ) : (
            <div className="flex scrollbar-thin max-h-80 flex-wrap gap-1.5 overflow-y-auto">
              {filteredTags.map((tag) => (
                <TagChipView
                  key={tag.id}
                  tag={tag}
                  onEdit={() => setEditingTag(tag)}
                  onRemove={() => setPendingTag(tag)}
                />
              ))}
            </div>
          )}
          {showTagControls && filteredTags.length > 0 ? (
            <p className="text-xs text-muted-foreground/70">
              Showing {filteredTags.length} of {tags.length}
            </p>
          ) : null}
        </CardContent>
      </Card>

      <EditTagDialog
        tag={editingTag}
        onClose={() => setEditingTag(null)}
        onSave={(id, patch, callbacks) => updateTag(id, patch, callbacks)}
      />

      <DeleteTagDialog
        subaccountId={subaccountId}
        tag={pendingTag}
        onClose={() => setPendingTag(null)}
        onConfirm={() => {
          if (pendingTag) deleteTag(pendingTag.id)
          setPendingTag(null)
        }}
      />
    </div>
  )
}

type ResourceLibrary = ReturnType<typeof useResourceLibrary>

/** The entity picker, value field and Add button; it owns the half-typed tag until it's added. */
function AddTagRow({ createTag }: { createTag: ResourceLibrary['createTag'] }) {
  const [entity, setEntity] = useState<NerEntity>('org')
  const [value, setValue] = useState('')

  function addTag() {
    const trimmed = value.trim()
    if (!trimmed) return
    createTag(entity, trimmed)
    setValue('')
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <select
        value={entity}
        onChange={(event) => setEntity(event.target.value as NerEntity)}
        aria-label="Tag entity type"
        className="h-9 rounded-md border border-input bg-background px-2 text-sm shadow-sm focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
      >
        {NER_ENTITIES.map((option) => (
          <option key={option} value={option}>
            {ENTITY_META[option].label}
          </option>
        ))}
      </select>
      <Input
        value={value}
        onChange={(event) => setValue(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault()
            addTag()
          }
        }}
        placeholder="Value — e.g. OpenAI"
        aria-label="Tag value"
        maxLength={TAG_VALUE_MAX}
        className="min-w-48 flex-1"
      />
      <Button type="button" variant="outline" onClick={addTag} disabled={!value.trim()}>
        <Plus className="size-4" /> Add tag
      </Button>
    </div>
  )
}
